import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, TrendingUp, Quote } from "lucide-react";
import { useLive } from "@/contexts/LiveContext";
import { getCategoryMeta } from "@/lib/categories";

export default function TrendingTicker({ items }) {
  const { subscribe } = useLive();
  const [flash, setFlash] = useState({ id: null, n: 0 });
  const deltas = useRef({});
  const [, force] = useState(0);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((msg) => {
      if (msg.type === "vote_update") {
        deltas.current[msg.topic_id] = (deltas.current[msg.topic_id] || 0) + 1;
        setFlash({ id: msg.topic_id, n: Date.now() });
        force((x) => x + 1);
      }
    });
  }, [subscribe]);

  if (!items?.length) return null;

  // Build pills: each debate, with its hot-take as a follow-up pill when present.
  const pills = [];
  items.forEach((t) => {
    pills.push({ kind: "debate", t });
    if (t.top_take) pills.push({ kind: "take", t });
  });
  const loop = [...pills, ...pills, ...pills];
  const duration = Math.max(1.2, pills.length * 0.42);

  return (
    <div className="ticker relative flex items-stretch bg-white rounded-xl shadow-card overflow-hidden mb-6" data-testid="trending-ticker">
      <div className="flex items-center gap-1.5 px-3 sm:px-4 bg-dark text-white text-[11px] font-bold tracking-wider uppercase shrink-0 z-10">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
        </span>
        <span className="hidden sm:inline">Live</span>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <div className="ticker-track flex items-center gap-6 py-2 whitespace-nowrap will-change-transform" style={{ animationDuration: `${duration}s` }}>
          {loop.map((p, i) => {
            const t = p.t;
            const meta = getCategoryMeta(t.category);
            const Icon = meta.Icon;
            const total = t.total_votes || 0;
            const pctA = total ? Math.round((t.votes_a / total) * 100) : 50;
            const leadA = pctA >= 50;
            const delta = deltas.current[t.topic_id] || 0;
            const isFlashing = flash.id === t.topic_id;
            const testid = i < pills.length ? `ticker-item-${i}` : undefined;

            if (p.kind === "take") {
              return (
                <Link key={`${t.topic_id}-take-${i}`} to={`/issue/${t.topic_id}`} data-testid={testid}
                  className="flex items-center gap-2 text-sm group shrink-0">
                  <Quote className="w-3.5 h-3.5 text-view-b shrink-0" />
                  <span className="italic text-neutral group-hover:text-dark transition-colors max-w-[22rem] truncate">“{t.top_take}”</span>
                </Link>
              );
            }
            return (
              <motion.div key={`${t.topic_id}-${i}`}
                animate={isFlashing ? { backgroundColor: ["rgba(255,195,0,0)", "rgba(255,195,0,0.18)", "rgba(255,195,0,0)"] } : {}}
                transition={{ duration: 1 }} className="rounded-lg">
                <Link to={`/issue/${t.topic_id}`} data-testid={testid} className="flex items-center gap-2 text-sm group shrink-0 px-1">
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: meta.color }} strokeWidth={2.4} />
                  <span className="font-medium text-dark group-hover:text-view-a transition-colors">{t.question}</span>
                  <span className="w-12 h-1.5 rounded-full overflow-hidden flex shrink-0">
                    <span className="bg-view-a h-full" style={{ width: `${pctA}%` }} />
                    <span className="bg-view-b h-full flex-1" />
                  </span>
                  <span className="text-[11px] font-bold tabular-nums shrink-0" style={{ color: leadA ? "#7B2CBF" : "#B8860B" }}>{leadA ? pctA : 100 - pctA}%</span>
                  {delta > 0 ? (
                    <motion.span key={delta} initial={{ scale: 1.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 tabular-nums shrink-0">
                      <TrendingUp className="w-3 h-3" />+{delta}
                    </motion.span>
                  ) : (
                    <span className="text-[11px] text-neutral tabular-nums shrink-0">{total.toLocaleString()}</span>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent ticker-fade" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent ticker-fade" />
      </div>
    </div>
  );
}
